import * as AgGrid from "ag-grid-community";
import * as datasource from "grid/datasource";
import * as dom from "utils/dom";
import * as gridUtils from "grid/utils";
import * as http from "utils/http";
import * as rowActions from "./row_actions";
import * as notifications from "utils/notifications";
import { ToggleAllRows } from "grid/toggleAllRows";

import {
  AMOUNT_COL_DEF,
  AMOUNT_FILTER_COL_DEF,
  DATE_FILTER_COL_DEF,
  DEFAULT_COL_DEF,
  ACTIONS_COL_DEF,
  TEXT_FILTER_COL_DEF,
} from "../../grid/columns";
import { BulkAction } from "grid/bulk_action";
import { GridController } from "grid/grid_controller";
import { debounce } from "utils/events";

interface Seller {
  id: string;
  name: string;
}

export default class extends GridController<Seller> {
  static targets = ["input", "suggestions"];
  connect() {
    super.connect();
  }

  addMessage(content, { type = "error" } = {}) {
    const template = document.getElementById("flash-warning");

    if (!template) return;

    const flashContainer = template.cloneNode(true);

    flashContainer.id = flashContainer.id + "-" + Date.now();

    const node = flashContainer.getElementsByClassName("undefined")[0];

    node.innerText = content.message;

    template.after(flashContainer);

    flashContainer.classList.remove("hidden");
  }

  clicked() {
    if (this.gridApi) {
      let data = [];
      this.gridApi.forEachNode((rowNode, index) => {
        data.push({ id: rowNode.data.id, name: rowNode.data.name });
      });

      fetch(this.element.dataset.patch_url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
        },
        body: JSON.stringify({ sellers: data }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log(data);
          this.addMessage(data);
        });
    }
  }

  override buildGrid() {
    const { i18nJson, paginationPageSize, sellers } = this.gridTarget.dataset;
    const { sellers_url, edit } = this.element.dataset;

    const colDefs: AgGrid.ColDef[] = [
      { field: "id", ...DEFAULT_COL_DEF, sortable: false },
      { field: "name", ...DEFAULT_COL_DEF, sortable: false },
    ];

    if (edit) {
      colDefs.push({
        field: "remove",
        ...DEFAULT_COL_DEF,
        cellClass: "actions-cell",
        onCellClicked: (params) => {
          const data = [];
          this.gridApi.forEachNode((rowNode, index) => {
            if (rowNode.data.id != params.data.id) data.push(rowNode.data);
          });

          this.gridApi.setGridOption("rowData", data);
        },
      });
    }

    const { maxBlocksInCache, onCellFocused, ...defaultOptions } = gridUtils.DEFAULT_GRID_OPTIONS;

    const options: AgGrid.GridOptions<Seller> = {
      ...defaultOptions,
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<Seller, any>),
      },
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n: JSON.parse(i18nJson) }),
      rowModelType: "clientSide",
      pagination: false,
      rowData: [],
      loading: false,
    };

    const gridApi = AgGrid.createGrid(this.gridTarget, {
      ...options,
    });

    fetch(`${sellers_url}.json`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
      },
    }).then((response) => {
      response.text().then((json) => {
        this.gridApi.setGridOption("rowData", JSON.parse(json).records);
      });
    });

    return gridApi;
  }

  // Seller Search Functionalities
  suggestions() {
    const query = this.inputTarget.value;
    const url = this.element.dataset.suggestions_url;

    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.requestSuggestions(query, url);
    }, 250);
  }

  requestSuggestions(query, url) {
    if (query.length === 0) {
      this.hideSuggestions();
      return;
    }

    this.showSuggestions();

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
      },
      body: JSON.stringify({ query: query }),
    }).then((response) => {
      response.text().then((html) => {
        document.body.insertAdjacentHTML("beforeend", html);
      });
    });
  }

  showSuggestions() {
    this.suggestionsTarget.classList.remove("hidden");
  }

  hideSuggestions() {
    this.suggestionsTarget.classList.add("hidden");
  }

  suggestionClicked(event) {
    let data = [];

    this.gridApi.forEachNode((rowNode, index) => {
      if (rowNode.data) {
        data.push({ id: rowNode.data.id, name: rowNode.data.name, remove: rowNode.data.remove });
      }
    });

    data.push({
      id: event.target.getAttribute("seller_id"),
      name: event.target.innerHTML,
      remove: event.target.getAttribute("button"),
    });

    this.gridApi.setGridOption("rowData", data);
  }
}
